<template>
    <section class="py-20 relative overflow-hidden">
        <div ref="blob1" class="blob-gradient blob1"></div>
        <div ref="blob2" class="blob-gradient blob2"></div>
        <div ref="blob3" class="blob-gradient blob3"></div>
        <div class="container mx-auto px-4 relative z-10">
            <div class="text-center mb-16">
                <h2 class="section-title text-slate-700 dark:text-slate-300">Latest Projects</h2>
                <p class="section-subtitle text-slate-700 dark:text-slate-400">Some projects I've worked on
                </p>
            </div>
            <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div v-for="project in projects" :key="project.id"
                    class="card group rounded-xl border-2 bg-secondary-100 dark:bg-slate-900/40 hover:bg-slate-200 dark:hover:bg-slate-800/40 dark:backdrop-blur-xl shadow-lg border-white/10">
                    <template v-if="project.video">
                        <VideoThumbnail :video-src="project.video" :title="project.title"
                            :description="project.description" class="mb-4" @click="onVideoClick(project)" />
                    </template>
                    <template v-else>
                        <img :src="project.image" :alt="project.title" class="w-full h-50 object-fit rounded-lg mb-4"
                            onerror="this.src='/assets/no-image.png'" />
                    </template>

                    <div class="flex items-center gap-2 mb-2">
                        <i :class="project.icon" class="text-lg text-primary-600 dark:text-primary-400"></i>
                        <span class="text-sm text-secondary-600 dark:text-secondary-400">{{ project.yearRange }}</span>
                    </div>
                    <h3 class="text-xl font-bold mb-2 text-slate-700 dark:text-slate-300">{{ project.title }}</h3>
                    <p class="text-secondary-600 dark:text-secondary-400 mb-4">{{ project.description }}</p>
                    <div class="flex flex-wrap gap-2 mb-4">
                        <span v-for="tech in project.technologies" :key="tech"
                            class="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full">
                            {{ tech }}
                        </span>
                    </div>
                    <div class="flex gap-2">
                        <template v-if="project.link">
                            <a :href="project.link" target="_blank" class="btn-primary text-sm flex items-center">
                                <i class="fas fa-link mr-1"></i>
                                Link
                            </a>
                        </template>
                        <template v-else-if="project.demo">
                            <a :href="project.demo" target="_blank" class="btn-primary text-sm flex items-center">
                                <i class="fas fa-external-link-alt mr-1"></i>
                                Demo
                            </a>
                        </template>
                        <template v-if="project.code">

                            <a :href="project.code" target="_blank" class="btn-secondary text-sm flex items-center">
                                <i class="fab fa-github mr-1"></i>
                                Code
                            </a>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useVideoModal } from '../plugins/videoModal'
import VideoThumbnail from '../components/VideoThumbnail.vue'

export default {
    components: {
        VideoThumbnail
    },
    data() {
        return { projects: [] }
    },
    async created() {
        const res = await fetch('/assets/projects.json')
        if (res.ok) {
            this.projects = await res.json()
        }
    },
    setup() {
        const blob1 = ref(null)
        const blob2 = ref(null)
        const blob3 = ref(null)
        const { openVideoModal } = useVideoModal()
        let mouseX = window.innerWidth / 2
        let mouseY = window.innerHeight / 2
        let animationId

        function onMouseMove(e) {
            mouseX = e.clientX
            mouseY = e.clientY
        }

        function animateBlobs() {
            const t = Date.now() * 0.001
            // Blob 1: radius 80px, sudut dinamis
            if (blob1.value) {
                const angle = t * 1.2
                const x = mouseX + Math.cos(angle) * 80 - 110
                const y = mouseY + Math.sin(angle) * 80 - 110
                blob1.value.style.left = `${x}px`
                blob1.value.style.top = `${y}px`
            }
            // Blob 2: radius 120px, sudut dinamis + offset
            if (blob2.value) {
                const angle = t * 0.9 + 2
                const x = mouseX + Math.cos(angle) * 120 - 110
                const y = mouseY + Math.sin(angle) * 120 - 110
                blob2.value.style.left = `${x}px`
                blob2.value.style.top = `${y}px`
            }
            // Blob 3: radius 60px, sudut dinamis + offset
            if (blob3.value) {
                const angle = t * 1.5 - 1
                const x = mouseX + Math.cos(angle) * 60 - 110
                const y = mouseY + Math.sin(angle) * 60 - 110
                blob3.value.style.left = `${x}px`
                blob3.value.style.top = `${y}px`
            }
            animationId = requestAnimationFrame(animateBlobs)
        }

        const onVideoClick = (project) => {
            console.log('Video clicked:', project.title)
        }

        onMounted(() => {
            window.addEventListener('mousemove', onMouseMove)
            animateBlobs()
        })
        onBeforeUnmount(() => {
            window.removeEventListener('mousemove', onMouseMove)
            cancelAnimationFrame(animationId)
        })
        return { blob1, blob2, blob3, onVideoClick }
    }
}
</script>

<style>
.blob-gradient {
    position: fixed;
    width: 200px;
    height: 200px;
    pointer-events: none;
    z-index: 0;
    border-radius: 50%;
    filter: blur(50px) brightness(1.0);
    opacity: 0.3;
    mix-blend-mode: lighten;
    transition: left 0.08s cubic-bezier(.55, 0, .1, 1), top 0.08s cubic-bezier(.55, 0, .1, 1);
}

.blob1 {
    background: radial-gradient(circle at 30% 30%, #60a5fa 0%, #a78bfa 60%, #f472b6 100%);
}

.blob2 {
    background: radial-gradient(circle at 70% 30%, #f472b6 0%, #fbbf24 60%, #60a5fa 100%);
}

.blob3 {
    background: radial-gradient(circle at 50% 70%, #a78bfa 0%, #34d399 60%, #f472b6 100%);
}
</style>
